/**
 * Configuración → Pagos CPE (`/configuracion/pagos-cpe`).
 *
 * Puerto de `API/Payments::cpe_methods_*` + `Payments_model` +
 * `views/navigation/settings/payments.php` («Establecer los tipos y destino de
 * los 3 principales métodos de pago»): cada método tiene un **id en el
 * facturador** y un **destino**, que es lo que se envía al emitir el comprobante
 * electrónico.
 *
 * La pantalla se parece a la del panel anterior: el nombre es de sólo lectura y
 * los dos valores se editan en la propia tabla, con un único botón *Guardar* que
 * manda todos los métodos de una vez.
 *
 * Es la única pantalla de Configuración que exige el grupo **admin** (no
 * `configuration`): así lo hacía `Settings::payments()` con `in_group(1)`.
 */

import { ArrowLeft, RotateCcw, Save, ShieldAlert, Wallet } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';

import { useMutarPagosCpe, usePagosCpe } from '../catalogos.api';
import type { MetodoCpe } from '../catalogos.types';

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

const CONTROL =
  'h-10 w-32 rounded-control border border-ink-200 bg-white px-2.5 text-sm text-ink-800 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Fila editable: se guarda como texto para poder dejarla vacía. */
interface FilaEditable {
  id: number;
  nombre: string;
  tipo: string;
  destino: string;
}

function aFilas(metodos: MetodoCpe[]): FilaEditable[] {
  return metodos.map((metodo) => ({
    id: metodo.id,
    nombre: metodo.nombre,
    tipo: metodo.tipo === null ? '' : String(metodo.tipo),
    destino: metodo.destino === null ? '' : String(metodo.destino),
  }));
}

export function PagosCpePage() {
  useDocumentTitle('Configuración · Pagos CPE');

  const metodos = usePagosCpe();
  const mutaciones = useMutarPagosCpe();

  const [filas, setFilas] = useState<FilaEditable[] | null>(null);
  const [aviso, setAviso] = useState<{ tono: 'success' | 'error'; texto: string } | null>(null);

  const sinPermiso = metodos.error instanceof ApiError && metodos.error.status === 403;
  const originales = metodos.data?.metodos ?? [];
  const actuales = filas ?? aFilas(originales);
  const cambio = JSON.stringify(actuales) !== JSON.stringify(aFilas(originales));

  const cabecera = (
    <header>
      <Link
        to="/configuracion"
        className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
      >
        <ArrowLeft className="size-3" aria-hidden="true" />
        Configuración
      </Link>
      <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
        <Wallet className="size-6 text-brand-500" aria-hidden="true" />
        Pagos CPE
      </h1>
      <p className="mt-1 max-w-2xl text-sm text-ink-500">
        Tipos y destino de los métodos de pago que se envían al facturador electrónico.
      </p>
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            Esta pantalla es sólo para <strong>administradores</strong>.
          </span>
        </Alert>
      </div>
    );
  }

  const cambiar = (id: number, campo: 'tipo' | 'destino', valor: string) => {
    setFilas(
      actuales.map((fila) => (fila.id === id ? { ...fila, [campo]: valor.replace(/[^\d]/g, '') } : fila)),
    );
  };

  const guardar = () => {
    setAviso(null);
    mutaciones.guardar.mutate(
      actuales.map((fila) => ({
        id: fila.id,
        tipo: fila.tipo === '' ? null : Number(fila.tipo),
        destino: fila.destino === '' ? null : Number(fila.destino),
      })),
      {
        onSuccess: (datos) => {
          setFilas(null);
          setAviso({ tono: 'success', texto: datos.mensaje });
        },
        onError: (error) =>
          setAviso({ tono: 'error', texto: mensajeDe(error, 'No se pudieron guardar los cambios.') }),
      },
    );
  };

  return (
    <div className="space-y-6">
      {cabecera}

      {aviso ? (
        <Alert variant={aviso.tono === 'success' ? 'success' : 'error'}>{aviso.texto}</Alert>
      ) : null}

      {metodos.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : metodos.error && !sinPermiso ? (
        <Alert variant="error" title="No se pudieron leer los métodos de pago">
          {mensajeDe(metodos.error, 'La API no respondió.')}
        </Alert>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Métodos de pago del comprobante</CardTitle>
            <Badge variant="neutral">{metodos.data?.total ?? 0} configurados</Badge>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[40rem] border-collapse text-sm">
                <thead>
                  <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                    <th className="pb-2">Método de pago</th>
                    <th className="w-44 pb-2">ID en el facturador</th>
                    <th className="w-44 pb-2">Destino en el facturador</th>
                  </tr>
                </thead>
                <tbody>
                  {actuales.map((fila) => (
                    <tr key={fila.id} className="border-t border-ink-100">
                      <td className="py-3 font-medium text-ink-900">{fila.nombre}</td>
                      <td className="py-3">
                        <input
                          type="number"
                          min={0}
                          step={1}
                          className={CONTROL}
                          aria-label={`ID en el facturador de ${fila.nombre}`}
                          value={fila.tipo}
                          placeholder="—"
                          onChange={(evento) => cambiar(fila.id, 'tipo', evento.target.value)}
                        />
                      </td>
                      <td className="py-3">
                        <input
                          type="number"
                          min={0}
                          step={1}
                          className={CONTROL}
                          aria-label={`Destino en el facturador de ${fila.nombre}`}
                          value={fila.destino}
                          placeholder="—"
                          onChange={(evento) => cambiar(fila.id, 'destino', evento.target.value)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                leftIcon={<Save className="size-3.5" aria-hidden="true" />}
                loading={mutaciones.guardar.isPending}
                disabled={!cambio}
                onClick={guardar}
              >
                Guardar
              </Button>
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<RotateCcw className="size-3.5" aria-hidden="true" />}
                disabled={!cambio || mutaciones.guardar.isPending}
                onClick={() => setFilas(null)}
              >
                Descartar
              </Button>
            </div>

            <Alert variant="info">
              Los números son los identificadores que usa el <strong>facturador electrónico</strong>{' '}
              para el tipo de método y su destino. Un campo vacío se envía como nulo, igual que en el
              panel anterior.
            </Alert>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
